import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/Button';
import { Card } from '@/shared/components/ui/Card';
import { ActiveFiltersSummary } from '@/shared/components/ui/ActiveFiltersSummary';
import { FrameEntryForm } from './components/FrameEntryForm';
import { FramesTable } from './components/FramesTable';
import { FrameFilters } from './components/FrameFilters';
import { useFrames } from './hooks/useFrames';
import { supabase } from '@/shared/services/supabase';
import type { FrameReplenishmentItem, FrameFilters as FrameFiltersType, Location, Manufacturer } from '@/shared/types/domain.types';

export function FramesToOrderPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFrame, setEditingFrame] = useState<FrameReplenishmentItem | undefined>();
  const [filters, setFilters] = useState<FrameFiltersType>(() => {
    // Load location filter from localStorage if available
    const storedLocationIds = localStorage.getItem('selectedLocationIds');
    const locationIds = storedLocationIds ? JSON.parse(storedLocationIds) : undefined;

    return {
      status: ['PENDING'], // Show only pending frames
      ...(locationIds && locationIds.length > 0 ? { location_ids: locationIds } : {}),
    };
  });
  const [showFilters, setShowFilters] = useState(() => {
    const stored = localStorage.getItem('framesToOrderFiltersVisible');
    return stored === null ? false : stored === 'true';
  });

  const { data: frames, isLoading, error } = useFrames(filters);

  // Fetch locations
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Location[];
    },
  });

  // Fetch manufacturers
  const { data: manufacturers } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('manufacturers')
        .select('*')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as Manufacturer[];
    },
  });

  // Build filter summary
  const filterSummary = useMemo(() => {
    const selectedLocations: { id: string; name: string }[] = [];
    const otherFilters: string[] = [];

    // Location filters (most important)
    if (filters.location_ids && filters.location_ids.length > 0) {
      filters.location_ids.forEach((id) => {
        const location = locations?.find((l) => l.id === id);
        if (location) selectedLocations.push({ id: location.id, name: location.name });
      });
    } else if (locations && locations.length > 0) {
      // Show all accessible locations if no filter is set
      selectedLocations.push(...locations.map((l) => ({ id: l.id, name: l.name })));
    }

    // Manufacturer filter
    if (filters.manufacturer_ids && filters.manufacturer_ids.length > 0) {
      const manufacturerNames = filters.manufacturer_ids
        .map((id) => manufacturers?.find((m) => m.id === id)?.name)
        .filter(Boolean);
      if (manufacturerNames.length > 0) {
        otherFilters.push(
          `Manufacturers: ${manufacturerNames.join(', ')}`
        );
      }
    }

    // Order type filter
    if (filters.order_type) {
      otherFilters.push(`Type: ${filters.order_type}`);
    }

    // Date range filter
    if (filters.date_from || filters.date_to) {
      const dateRange = [];
      if (filters.date_from) dateRange.push(`from ${filters.date_from}`);
      if (filters.date_to) dateRange.push(`to ${filters.date_to}`);
      otherFilters.push(`Date range: ${dateRange.join(' ')}`);
    }

    // Search filter
    if (filters.search) {
      otherFilters.push(`Search: "${filters.search}"`);
    }

    return { selectedLocations, otherFilters };
  }, [filters, locations, manufacturers]);

  const handleLocationAdd = (locationId: string) => {
    const currentLocationIds = filters.location_ids || [];
    const newLocationIds = [...currentLocationIds, locationId];

    // Save to localStorage
    localStorage.setItem('selectedLocationIds', JSON.stringify(newLocationIds));

    setFilters({
      ...filters,
      location_ids: newLocationIds,
    });
  };

  const handleLocationRemove = (locationId: string) => {
    let currentLocationIds = filters.location_ids || [];

    // If no filter is set (showing all locations), start with all location IDs
    if (currentLocationIds.length === 0 && locations) {
      currentLocationIds = locations.map((l) => l.id);
    }

    const newLocationIds = currentLocationIds.filter((id) => id !== locationId);

    // If no locations are selected, remove the filter entirely to show all locations
    if (newLocationIds.length === 0) {
      // Clear from localStorage
      localStorage.removeItem('selectedLocationIds');

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { location_ids, ...restFilters } = filters;
      setFilters(restFilters);
    } else {
      // Save to localStorage
      localStorage.setItem('selectedLocationIds', JSON.stringify(newLocationIds));

      setFilters({
        ...filters,
        location_ids: newLocationIds,
      });
    }
  };

  const handleAddFrame = () => {
    setEditingFrame(undefined);
    setIsFormOpen(true);
  };

  const handleEditFrame = (frame: FrameReplenishmentItem) => {
    setEditingFrame(frame);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingFrame(undefined);
  };

  const toggleFilters = () => {
    const newValue = !showFilters;
    setShowFilters(newValue);
    localStorage.setItem('framesToOrderFiltersVisible', String(newValue));
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Frames to Order</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage frames that need to be ordered for stock replenishment or patient orders
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="secondary" onClick={toggleFilters}>
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button onClick={handleAddFrame}>
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Frame
          </Button>
        </div>
      </div>

      {/* Active Filters Summary (shown when filters are hidden) */}
      {!showFilters && (
        <ActiveFiltersSummary
          selectedLocations={filterSummary.selectedLocations}
          availableLocations={locations?.map((l) => ({ id: l.id, name: l.name })) || []}
          otherFilters={filterSummary.otherFilters}
          onLocationAdd={handleLocationAdd}
          onLocationRemove={handleLocationRemove}
          onShowAllFilters={toggleFilters}
        />
      )}

      {/* Filters */}
      {showFilters && <FrameFilters filters={filters} onFilterChange={setFilters} />}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            Error loading frames: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {/* Table */}
      <Card>
        <FramesTable
          frames={frames || []}
          onEdit={handleEditFrame}
          isLoading={isLoading}
        />
      </Card>

      {/* Add/Edit Form Modal */}
      <FrameEntryForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        frame={editingFrame}
      />
    </div>
  );
}
